import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Listing } from '../types';
import { fetchListingById, submitClaim } from '../services/api';
import { trackAnalyticsEvent } from '../services/analytics';

const ClaimListingPage: React.FC = () => {
  const { listingId } = useParams<{ listingId: string }>();
  const [listing, setListing] = useState<Listing | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [businessRole, setBusinessRole] = useState('Owner');
  const [verificationNotes, setVerificationNotes] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const data = await fetchListingById(Number(listingId));
      setListing(data);
      setLoading(false);
    };
    load();
    trackAnalyticsEvent('claim_start', { placement: 'claim_page' });
  }, [listingId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!name || !email || !phone) {
      setError('Please fill in all required fields.');
      return;
    }
    setSubmitting(true);
    try {
      await submitClaim({
        listingId: Number(listingId),
        name,
        email,
        phone,
        businessRole,
        verificationNotes,
      });
      trackAnalyticsEvent('claim_submit', { placement: 'claim_page' });
      setSuccess(true);
    } catch {
      setError('Failed to submit claim. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ maxWidth: '700px', margin: '0 auto', padding: '60px 20px', textAlign: 'center', color: '#666' }}>
        Loading listing...
      </div>
    );
  }

  if (!listing) {
    return (
      <div style={{ maxWidth: '700px', margin: '0 auto', padding: '60px 20px', textAlign: 'center' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>❌</div>
        <h2>Listing not found</h2>
        <Link to="/" style={{ color: '#1a237e', fontWeight: 600 }}>← Back to Home</Link>
      </div>
    );
  }

  if (success) {
    return (
      <div style={{ maxWidth: '700px', margin: '0 auto', padding: '60px 20px', textAlign: 'center' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
        <h2 style={{ color: '#2e7d32' }}>Claim Submitted Successfully</h2>
        <p style={{ color: '#666', marginBottom: '24px' }}>
          Your claim for <strong>{listing.name}</strong> has been submitted. We will review it and get back to you soon.
        </p>
        <Link to={`/listing/${listing.listingId}`} style={{
          display: 'inline-block', padding: '12px 32px', background: '#1a237e',
          color: '#fff', borderRadius: '8px', textDecoration: 'none', fontWeight: 600,
        }}>View Listing</Link>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '700px', margin: '0 auto', padding: '24px 20px' }}>
      <Link to={`/listing/${listing.listingId}`} style={{ color: '#1a237e', textDecoration: 'none', fontSize: '14px', fontWeight: 600 }}>
        ← Back to Listing
      </Link>

      <div style={{
        marginTop: '16px', padding: '24px', background: '#f8f9fa',
        borderRadius: '12px', border: '1px solid #e0e0e0', marginBottom: '24px',
      }}>
        <h2 style={{ margin: '0 0 4px', fontSize: '22px', color: '#1a237e' }}>{listing.name}</h2>
        {listing.address && (
          <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
            📍 {listing.address.city}, {listing.address.region}
          </p>
        )}
      </div>

      <div style={{
        padding: '32px', background: '#fff', borderRadius: '12px',
        border: '1px solid #e0e0e0', boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      }}>
        <h1 style={{ fontSize: '24px', margin: '0 0 8px' }}>Claim This Listing</h1>
        <p style={{ color: '#666', margin: '0 0 24px', fontSize: '14px' }}>
          Verify your ownership to manage this listing, respond to reviews, and access analytics.
        </p>

        {error && (
          <div style={{
            padding: '12px 16px', background: '#ffebee', color: '#c62828',
            borderRadius: '8px', marginBottom: '16px', fontSize: '14px',
          }}>{error}</div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Name *</label>
            <input style={inputStyle} type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Your full name" />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Email *</label>
            <input style={inputStyle} type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Phone *</label>
            <input style={inputStyle} type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="(555) 123-4567" />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Business Role</label>
            <select style={inputStyle} value={businessRole} onChange={e => setBusinessRole(e.target.value)}>
              <option value="Owner">Owner</option>
              <option value="Manager">Manager</option>
              <option value="Authorized Representative">Authorized Representative</option>
            </select>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={labelStyle}>Verification Notes</label>
            <textarea
              style={{ ...inputStyle, minHeight: '100px', resize: 'vertical' }}
              value={verificationNotes}
              onChange={e => setVerificationNotes(e.target.value)}
              placeholder="Any details to help us verify your identity (e.g., business license number, official website)"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            style={{
              width: '100%', padding: '14px', background: submitting ? '#9fa8da' : '#1a237e',
              color: '#fff', border: 'none', borderRadius: '8px', fontSize: '16px',
              fontWeight: 700, cursor: submitting ? 'not-allowed' : 'pointer',
            }}
          >
            {submitting ? 'Submitting...' : 'Submit Claim'}
          </button>
        </form>
      </div>
    </div>
  );
};

const labelStyle: React.CSSProperties = {
  display: 'block', marginBottom: '6px', fontSize: '14px',
  fontWeight: 600, color: '#333',
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 12px', border: '1px solid #e0e0e0',
  borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box',
  outline: 'none',
};

export default ClaimListingPage;
